import { useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { uploadEvidenceFile } from './evidenceApi';
import { EVIDENCE_ACCEPT, EVIDENCE_LIMITS_TEXT, EvidenceItem, EvidenceOwnerType, evidenceFileError, evidenceLabel } from './evidenceLogic';

// Attach bills / vouchers to an expense or receipt. The button is a real <button> (Tab to it, Enter or Space opens the file dialog); each
// attached file is a chip that Backspace / Delete removes from THIS form (the stored file stays unlinked and is simply not used).
// Files are uploaded when picked and linked to the record when the form is saved.

export function EvidencePicker({ branchId, ownerType, items, onChange, disabled }: {
  branchId: string; ownerType: EvidenceOwnerType; items: EvidenceItem[]; onChange: (next: EvidenceItem[]) => void; disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // the same file can be picked again later
    if (files.length === 0) return;
    setBusy(true);
    const errs: string[] = [];
    let held = items;
    for (const f of files) {
      const problem = evidenceFileError(f, held.length);
      if (problem) { errs.push(problem); continue; }
      try {
        const saved = await uploadEvidenceFile(branchId, ownerType, f);
        held = [...held, saved];
        onChange(held);
      } catch (err: any) {
        errs.push(`${f.name}: ${err?.response?.data?.error ?? 'could not be uploaded'}`);
      }
    }
    setErrors(errs);
    setBusy(false);
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" disabled={disabled || busy || !branchId} onClick={() => input.current?.click()} aria-describedby="evidence-limits">
          {busy ? 'Uploading...' : 'Attach file'}
        </Button>
        <Typography id="evidence-limits" variant="caption" color="text.secondary">{EVIDENCE_LIMITS_TEXT}</Typography>
        <input ref={input} type="file" multiple accept={EVIDENCE_ACCEPT} onChange={pick} tabIndex={-1} aria-hidden="true" data-testid="evidence-input" style={{ display: 'none' }} />
      </Stack>
      {items.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }} aria-label="Attached files">
          {items.map((f) => <Chip key={f.id} size="small" label={evidenceLabel(f)} onDelete={disabled ? undefined : () => onChange(items.filter((x) => x.id !== f.id))} />)}
        </Stack>
      )}
      {errors.map((m) => <Alert key={m} severity="error" sx={{ mt: 0.5, py: 0 }}>{m}</Alert>)}
    </Box>
  );
}
